import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import type { Account } from '@domain/finance'
import type { Cents } from '@shared/money/cents'
import type {
  AccountPatch,
  AccountRepository,
  NewAccount,
} from '@domain/ports/finance-repository'
import {
  optionalCents,
  optionalText,
  requireIso,
  requireOneOf,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * As contas da casa: leitura, criação e edição.
 *
 * `firestore.rules` dá `read, write` livres a membro em `accounts` — não há
 * campo derivado a proteger ali —, então este é um dos poucos repositórios em
 * que o cliente escreve de verdade. O que ele **não** escreve é o que o
 * documento não deveria ter: `provider` de conta criada à mão é `'manual'` e os
 * dois `providerId` são `null`, porque conta de provider nasce da ingestão da
 * Fase 3 e não deste formulário.
 *
 * O `householdId` sai do **caminho**, e não do campo: o documento vive em
 * `households/{hid}/accounts/{id}`, e o caminho é o que a regra consulta. Mesma
 * razão pela qual `toMember` usa o id do documento e ignora o campo `userId`.
 */

const TYPES = ['checking', 'savings', 'payment', 'cash'] as const
const PROVIDERS = ['pluggy', 'manual'] as const
const STATUSES = ['active', 'archived', 'sync_error'] as const

function accountsPath(): string {
  return `households/${HOUSEHOLD_ID}/accounts`
}

/**
 * O invariante que `Docs/02-Dominio/Account.md` nomeia e o tipo não expressa:
 * **dinheiro em espécie não tem saldo consultável.** Não há como saber quanto
 * há no bolso, então `balanceCents` de `'cash'` é `null` — e um número ali seria
 * um saldo que ninguém mantém, envelhecendo em silêncio ao lado dos que o
 * provider atualiza.
 *
 * Cobrado na escrita porque é onde dá para recusar. Na leitura, um documento
 * que já esteja assim é devolvido como está: a leitura não conserta o banco, e
 * esconder o valor faria a tela mostrar `null` sobre um documento que tem
 * número — o pior dos dois mundos para quem for investigar.
 */
function assertCashHasNoBalance(input: { type: Account['type']; balanceCents?: Cents | null }): void {
  if (input.type === 'cash' && input.balanceCents !== undefined && input.balanceCents !== null) {
    throw new Error(
      "account-repository: conta 'cash' não tem saldo consultável — balanceCents precisa ser null.",
    )
  }
}

/** Firestore → domínio. O id e o `householdId` saem do caminho. */
export function toAccount(id: string, data: DocumentData): Account {
  const where = `accounts/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    name: textOr(data['name'], 'Conta sem nome'),
    institution: textOr(data['institution'], ''),
    type: requireOneOf(data['type'], TYPES, `${where}.type`),
    // Um valor só no tipo do domínio. Ler do documento só criaria uma forma de
    // estar errado — mesma razão de `timezone` e `currency` em `toHousehold`.
    currency: 'BRL',
    provider: requireOneOf(data['provider'], PROVIDERS, `${where}.provider`),
    providerItemId: optionalText(data['providerItemId']),
    providerAccountId: optionalText(data['providerAccountId']),
    balanceCents: optionalCents(data['balanceCents'], `${where}.balanceCents`),
    balanceUpdatedAt: toIso(data['balanceUpdatedAt']),
    status: requireOneOf(data['status'], STATUSES, `${where}.status`),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

/**
 * Domínio → Firestore, para o `create`. Função exportada, e não um objeto
 * montado dentro do `addDoc`, porque o payload é o que o teste inspeciona: é
 * assim que "o cliente não escreve campo de provider" deixa de ser comentário.
 *
 * `serverTimestamp()` nos três instantes, e não o relógio do navegador: o
 * `createdAt` de dois aparelhos com relógios diferentes ordenaria errado, e o
 * lint proíbe `new Date()` sem argumento em `src/**` justamente por isso.
 */
export function accountDocument(input: NewAccount): DocumentData {
  assertCashHasNoBalance(input)
  return {
    name: input.name,
    institution: input.institution,
    type: input.type,
    currency: 'BRL',
    provider: 'manual',
    providerItemId: null,
    providerAccountId: null,
    balanceCents: input.balanceCents,
    balanceUpdatedAt: input.balanceCents === null ? null : serverTimestamp(),
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
}

/**
 * O patch, com os campos ausentes **fora do objeto**.
 *
 * `updateDoc` com uma chave `undefined` lança (`Unsupported field value:
 * undefined`), e um patch parcial vindo de um formulário tem sempre chaves
 * ausentes — então montar `{ name: patch.name }` cru quebraria toda edição que
 * não mexesse no nome. `balanceUpdatedAt` acompanha `balanceCents`: saldo novo
 * sem carimbo novo é um saldo que finge ser mais antigo do que é.
 */
export function accountPatchDocument(patch: AccountPatch): DocumentData {
  const document: DocumentData = { updatedAt: serverTimestamp() }
  if (patch.name !== undefined) document['name'] = patch.name
  if (patch.institution !== undefined) document['institution'] = patch.institution
  if (patch.status !== undefined) document['status'] = patch.status
  if (patch.providerAccountId !== undefined) {
    document['providerAccountId'] = patch.providerAccountId
    // `provider` acompanha o vínculo: uma conta com id do provider e
    // `provider: 'manual'` seria mentira, e é o campo que a UI usa para dizer
    // de onde veio o dado.
    document['provider'] = patch.providerAccountId === null ? 'manual' : 'pluggy'
  }
  if (patch.balanceCents !== undefined) {
    document['balanceCents'] = patch.balanceCents
    document['balanceUpdatedAt'] = patch.balanceCents === null ? null : serverTimestamp()
  }
  return document
}

/**
 * **Sem `orderBy`, e a ausência é deliberada.** Uma consulta com
 * `orderBy('name')` não devolve documento **sem** o campo `name` — o Firestore
 * não ordena o que não está no índice —, então uma conta a que faltasse o nome
 * sumiria da tela sem erro nenhum, e o `textOr` que existe justamente para
 * tolerar essa falta nunca rodaria. Uma conta invisível é pior do que uma conta
 * feia.
 *
 * Sem `orderBy` a ordem é a do id do documento, que é estável e não depende de
 * campo nenhum. Quem quiser a lista em ordem alfabética ordena na tela, onde a
 * ausência do nome já virou um texto.
 */
async function listAccounts(db: Firestore): Promise<Account[]> {
  const snap = await getDocs(collection(db, accountsPath()))
  return snap.docs.map((account) => toAccount(account.id, account.data()))
}

async function createAccount(db: Firestore, input: NewAccount): Promise<string> {
  const ref = await addDoc(collection(db, accountsPath()), accountDocument(input))
  return ref.id
}

async function updateAccount(
  db: Firestore,
  id: string,
  patch: AccountPatch,
): Promise<void> {
  // **O invariante do saldo de 'cash' NÃO é cobrado aqui**, e isto está escrito
  // em vez de resolvido: a checagem precisa do `type` já gravado, que o patch
  // não carrega, e ler o documento antes de escrever custaria uma ida ao
  // servidor por edição sem fechar corrida nenhuma. O `create` cobra; o
  // `update` de uma conta 'cash' com saldo passa. Quem fecha é a tela da
  // Task 11, não oferecendo o campo de saldo para 'cash'.
  await updateDoc(doc(db, accountsPath(), id), accountPatchDocument(patch))
}

/** A porta com o `db` já preso, para a composição em `src/main.tsx`. */
export function createFirebaseAccountRepository(db: Firestore): AccountRepository {
  return {
    list: () => listAccounts(db),
    create: (input) => createAccount(db, input),
    update: (id, patch) => updateAccount(db, id, patch),
  }
}
